import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { number, thaiDateTime } from '../lib/format'

/**
 * Circuit breakers (part 92). Unlike the dependency probe on the Health page — which keeps its
 * breaker in one process's memory — every row here is read from platform.breakers. Two API
 * instances looking at this screen therefore see the same state, and a reset on one affects all
 * of them on the next call.
 */

type BreakerState = 'closed' | 'open' | 'half_open'

interface BreakerRow {
  target: string
  state: BreakerState
  consecutive_failures: number
  reopens: number
  cooldown_until: string | null
  probe_expires_at: string | null
  half_open_successes: number
  last_failure_at: string | null
  last_success_at: string | null
  last_transition_at: string | null
  last_from_state?: string
  updated_at: string
  retry_after_seconds?: number
}

interface BreakerPolicy {
  failure_threshold: number
  success_threshold: number
  cooldown_seconds: number
  max_cooldown_seconds: number
  probe_lease_seconds: number
}

interface BreakerList {
  breakers: BreakerRow[]
  policy: BreakerPolicy
  checked_at: string
}

interface BreakerEvent {
  id: string
  target: string
  from_state: BreakerState
  to_state: BreakerState
  reason: string
  actor?: string
  occurred_at: string
}

interface BreakerHistory {
  target: string
  events: BreakerEvent[]
  count: number
}

interface BreakerRecord {
  breaker: BreakerRow
  applied: boolean
  transitioned: boolean
  from_state?: string
  to_state: string
  reason?: string
}

interface ResetResult {
  breaker: BreakerRow
  transitioned: boolean
  from_state?: string
  to_state: string
  reset_at: string
}

const LIST_KEY = ['breakers']
const historyKey = (target: string) => ['breaker-history', target]

const stateLabel: Record<BreakerState, string> = {
  closed: 'ปิดวงจร · ใช้งานได้',
  open: 'เปิดวงจร · ตัดการเรียก',
  half_open: 'ครึ่งวงจร · ทดลอง',
}

const stateTone: Record<BreakerState, string> = {
  closed: 'sev-low',
  open: 'sev-critical',
  half_open: 'sev-high',
}

const stateShort: Record<BreakerState, string> = {
  closed: 'ปิดวงจร',
  open: 'เปิดวงจร',
  half_open: 'ครึ่งวงจร',
}

const stateDetail: Record<BreakerState, string> = {
  closed: 'คำขอผ่านได้ตามปกติ ระบบนับความล้มเหลวติดกันและจะเปิดวงจรเมื่อถึงเกณฑ์',
  open: 'หยุดส่งคำขอถึงเป้าหมายนี้จนกว่าคูลดาวน์จะครบ จากนั้นจะอนุญาตคำขอทดลองหนึ่งครั้ง',
  half_open: 'อนุญาตคำขอทดลองได้ครั้งละหนึ่งคำขอ ถ้าสำเร็จจะปิดวงจร และถ้าล้มเหลวจะเปิดอีกครั้งด้วยคูลดาวน์ที่ยาวขึ้น',
}

const reasonLabel: Record<string, string> = {
  failure_threshold: 'ล้มเหลวติดกันถึงเกณฑ์',
  cooldown_elapsed: 'คูลดาวน์ครบ เปิดให้ทดลอง',
  probe_failed: 'คำขอทดลองล้มเหลว',
  probe_succeeded: 'คำขอทดลองสำเร็จ',
  admin_reset: 'ผู้ดูแลรีเซ็ต',
  transition: 'เปลี่ยนสถานะ',
}

const outcomeLabel: Record<string, string> = {
  success: 'สำเร็จ',
  failure: 'ล้มเหลว',
}

function durationText(seconds: number): string {
  if (seconds <= 0) return 'พร้อมทดสอบ'
  if (seconds < 60) return `${seconds} วินาที`
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return rest === 0 ? `${minutes} นาที` : `${minutes} นาที ${rest} วินาที`
}

function remainingSeconds(iso: string | null, now: number): number {
  if (!iso) return 0
  const diff = new Date(iso).getTime() - now
  return diff > 0 ? Math.ceil(diff / 1000) : 0
}

function timeText(iso: string | null | undefined): string {
  return iso ? thaiDateTime(iso) : '—'
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function stateName(value: string | undefined): string {
  if (!value) return '—'
  return stateShort[value as BreakerState] ?? value
}

// A one-second tick is enough for the cooldown countdown and costs nothing when the tab is
// hidden because the browser throttles the interval.
function useNow(): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])
  return now
}

export function Breakers() {
  const qc = useQueryClient()
  const now = useNow()
  const [selected, setSelected] = useState<string | null>(null)
  const [pending, setPending] = useState<'reset' | 'success' | 'failure' | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const list = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => api<BreakerList>('/v1/breakers'),
    // State changes when a guarded call reports an outcome, which can happen on any replica.
    refetchInterval: 10_000,
    staleTime: 5_000,
  })

  const rows = list.data?.breakers ?? []
  const policy = list.data?.policy

  useEffect(() => {
    if (selected && rows.some((row) => row.target === selected)) return
    setSelected(rows.length > 0 ? rows[0].target : null)
  }, [rows, selected])

  const history = useQuery({
    queryKey: historyKey(selected ?? ''),
    queryFn: () => api<BreakerHistory>(`/v1/breakers/${encodeURIComponent(selected ?? '')}/history?limit=30`),
    enabled: selected !== null,
    refetchInterval: 10_000,
  })

  const selectedRow = useMemo(
    () => rows.find((row) => row.target === selected) ?? null,
    [rows, selected],
  )

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: LIST_KEY })
    if (selected) void qc.invalidateQueries({ queryKey: historyKey(selected) })
  }

  const record = useMutation({
    mutationFn: (input: { target: string; outcome: 'success' | 'failure' }) =>
      apiWrite<BreakerRecord>(
        `/v1/breakers/${encodeURIComponent(input.target)}/outcomes`,
        'POST',
        { outcome: input.outcome },
      ),
    onSuccess: (result, input) => {
      setActionError(null)
      if (!result.applied) {
        setNotice(
          `บันทึกผล “${outcomeLabel[input.outcome]}” ของ ${input.target} แล้ว แต่ไม่มีผลกับสถานะปัจจุบัน ` +
          `(คำขอนี้อาจมาจากช่วงเวลาก่อนวงจรเปิด หรือเป็นโทเคนทดลองที่หมดอายุ)`,
        )
      } else if (result.transitioned) {
        setNotice(
          `เปลี่ยนสถานะ ${input.target}: ${stateName(result.from_state)} → ${stateName(result.to_state)} ` +
          `(${reasonLabel[result.reason ?? ''] ?? result.reason ?? 'เปลี่ยนสถานะ'})`,
        )
      } else {
        setNotice(`บันทึกผล “${outcomeLabel[input.outcome]}” ของ ${input.target} แล้ว สถานะยังเป็น ${stateName(result.to_state)}`)
      }
      setPending(null)
      void qc.invalidateQueries({ queryKey: LIST_KEY })
      void qc.invalidateQueries({ queryKey: historyKey(input.target) })
    },
    onError: (err) => {
      setPending(null)
      setActionError(errorText(err, 'บันทึกผลการเรียกไม่สำเร็จ'))
    },
  })

  const reset = useMutation({
    mutationFn: (target: string) =>
      apiWrite<ResetResult>(`/v1/breakers/${encodeURIComponent(target)}/reset`, 'POST'),
    onSuccess: (result) => {
      setActionError(null)
      setNotice(
        result.transitioned
          ? `รีเซ็ต ${result.breaker.target} แล้ว: ${stateName(result.from_state)} → ปิดวงจร และล้างประวัติการเปิดวงจร`
          : `รีเซ็ต ${result.breaker.target} แล้ว ตัวตัดวงจรอยู่ในสถานะปิดและตัวนับถูกล้าง`,
      )
      setPending(null)
      void qc.invalidateQueries({ queryKey: LIST_KEY })
      void qc.invalidateQueries({ queryKey: historyKey(result.breaker.target) })
    },
    onError: (err) => {
      setPending(null)
      setActionError(errorText(err, 'รีเซ็ตตัวตัดวงจรไม่สำเร็จ'))
    },
  })

  const counts = useMemo(() => {
    const base = { closed: 0, open: 0, half_open: 0 }
    for (const row of rows) base[row.state] += 1
    return base
  }, [rows])

  const busy = record.isPending || reset.isPending

  return (
    <>
      <PageHead
        title="ตัวตัดวงจรและกู้คืนอัตโนมัติ"
        sub="สถานะกลางในฐานข้อมูล · ทุกอินสแตนซ์ของ API เห็นวงจรเดียวกัน · เปิดเมื่อล้มเหลวติดกัน แล้วทดลองซ้ำเมื่อคูลดาวน์ครบ"
        actions={
          <div className="row" style={{ gap: 10 }}>
            <span className="dim" style={{ fontSize: 11.5 }}>
              {list.data ? `ตรวจล่าสุด ${thaiDateTime(list.data.checked_at)}` : 'ยังไม่ได้รับข้อมูล'}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={refresh} disabled={list.isFetching}>
              {list.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
            </button>
          </div>
        }
      />

      {notice ? (
        <div className="card card-pad" role="status" style={{ marginBottom: 14, borderColor: 'var(--color-hairline-strong)' }}>
          <span className="dim" style={{ fontSize: 12.5 }}>{notice}</span>
        </div>
      ) : null}

      {actionError ? (
        <div className="card card-pad" role="alert" style={{ marginBottom: 14, borderColor: 'var(--color-crimson)' }}>
          <span style={{ fontSize: 12.5, color: 'var(--color-crimson)' }}>{actionError}</span>
        </div>
      ) : null}

      <div className="grid-4 stagger">
        <Stat
          label="เป้าหมายที่ตรวจพบ"
          value={rows.length}
          unit="รายการ"
          foot="แถวจะปรากฏเมื่อมีการเรียกผ่านตัวตัดวงจรครั้งแรก"
        />
        <Stat
          label="เปิดวงจรอยู่"
          value={counts.open}
          unit="รายการ"
          foot={counts.open > 0 ? 'คำขอถูกปฏิเสธจนกว่าคูลดาวน์จะครบ' : 'ไม่มีเป้าหมายที่ถูกตัดการเรียก'}
        />
        <Stat
          label="กำลังทดลอง"
          value={counts.half_open}
          unit="รายการ"
          foot="อนุญาตครั้งละหนึ่งคำขอทดลอง"
        />
        <Stat
          label="ปิดวงจร"
          value={counts.closed}
          unit="รายการ"
          foot={policy ? `เปิดเมื่อล้มเหลวติดกัน ${policy.failure_threshold} ครั้ง` : 'พร้อมใช้งาน'}
        />
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="row" style={{ padding: '16px 20px', gap: 12 }}>
          <span className="card-label">ทะเบียนตัวตัดวงจร</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            {policy
              ? `คูลดาวน์เริ่มต้น ${durationText(policy.cooldown_seconds)} · เพิ่มขึ้นสูงสุด ${durationText(policy.max_cooldown_seconds)} · ทดลองได้ไม่เกิน ${policy.probe_lease_seconds} วินาที`
              : 'กำลังอ่านนโยบาย'}
          </span>
        </div>

        {list.isLoading ? (
          <div style={{ padding: 20 }} className="stack"><Skel h={48} /><Skel h={48} /><Skel h={48} /></div>
        ) : list.isError ? (
          <div style={{ padding: 20 }}>
            <Empty
              title="โหลดทะเบียนตัวตัดวงจรไม่สำเร็จ"
              body={errorText(list.error, 'เรียก /v1/breakers ไม่สำเร็จ — ตรวจสอบว่าเส้นทางถูก mount แล้ว')}
            />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 20 }}>
            <Empty
              title="ยังไม่มีตัวตัดวงจรที่บันทึกไว้"
              body="เมื่อมีการเรียกผ่านตัวตัดวงจรครั้งแรก ระบบจะสร้างแถวใน platform.breakers และแสดงสถานะที่นี่ สถานะนี้ใช้ร่วมกันทุกอินสแตนซ์ของ API"
            />
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>เป้าหมาย</th>
                  <th style={{ width: 190 }}>สถานะ</th>
                  <th style={{ width: 130 }}>ล้มเหลวติดกัน</th>
                  <th style={{ width: 170 }}>คูลดาวน์เหลือ</th>
                  <th style={{ width: 100 }}>เปิดซ้ำ</th>
                  <th style={{ width: 190 }}>เปลี่ยนล่าสุด</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const active = row.target === selected
                  const until = row.state === 'open' ? row.cooldown_until : row.state === 'half_open' ? row.probe_expires_at : null
                  const remain = remainingSeconds(until, now)
                  return (
                    <tr
                      key={row.target}
                      onClick={() => {
                        setSelected(row.target)
                        setPending(null)
                        setActionError(null)
                      }}
                      style={{ cursor: 'pointer', background: active ? 'rgba(200, 169, 106, 0.05)' : undefined }}
                    >
                      <td>
                        <div className="mono" style={{ fontSize: 12, color: 'var(--color-ivory)' }}>{row.target}</div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                          {row.last_from_state ? `มาจาก ${stateName(row.last_from_state)}` : 'ไม่เคยเปลี่ยนสถานะ'}
                        </div>
                      </td>
                      <td>
                        <span className={`sev ${stateTone[row.state]}`}>
                          <i className="sev-dot" />
                          {stateLabel[row.state]}
                        </span>
                      </td>
                      <td className="tnum">
                        {number(row.consecutive_failures)}
                        {policy ? <span className="dim"> / {policy.failure_threshold}</span> : null}
                      </td>
                      <td className="tnum dim" style={{ fontSize: 11.5 }}>
                        {until ? (remain > 0 ? durationText(remain) : 'พร้อมทดสอบ') : '—'}
                      </td>
                      <td className="tnum">{number(row.reopens)}</td>
                      <td className="dim" style={{ fontSize: 11 }}>
                        {timeText(row.last_transition_at)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rows.length > 0 ? (
        <div className="grid-2" style={{ marginTop: 18 }}>
          <div className="card card-pad">
            <div className="row" style={{ marginBottom: 14, gap: 10 }}>
              <span className="card-label">รายละเอียดเป้าหมาย</span>
              <span className="dim mono" style={{ marginLeft: 'auto', fontSize: 11 }}>{selected ?? '—'}</span>
            </div>

            {!selectedRow ? (
              <div className="dim" style={{ fontSize: 12.5 }}>เลือกเป้าหมายจากตารางเพื่อดูรายละเอียด</div>
            ) : (
              <>
                <div className="row" style={{ gap: 10, marginBottom: 12 }}>
                  <span className={`sev ${stateTone[selectedRow.state]}`}>
                    <i className="sev-dot" />
                    {stateLabel[selectedRow.state]}
                  </span>
                  {selectedRow.retry_after_seconds ? (
                    <span className="chip">ลองใหม่ใน {durationText(selectedRow.retry_after_seconds)}</span>
                  ) : null}
                </div>
                <p className="dim" style={{ fontSize: 12.5, lineHeight: 1.85, marginBottom: 16 }}>
                  {stateDetail[selectedRow.state]}
                </p>

                <div className="stack" style={{ gap: 9, fontSize: 12 }}>
                  <div className="row" style={{ gap: 10 }}>
                    <span className="dim">ล้มเหลวติดกัน</span>
                    <span className="tnum" style={{ marginLeft: 'auto' }}>
                      {selectedRow.consecutive_failures}{policy ? ` / ${policy.failure_threshold}` : ''}
                    </span>
                  </div>
                  <div className="row" style={{ gap: 10 }}>
                    <span className="dim">จำนวนครั้งที่เปิดวงจร</span>
                    <span className="tnum" style={{ marginLeft: 'auto' }}>{selectedRow.reopens}</span>
                  </div>
                  <div className="row" style={{ gap: 10 }}>
                    <span className="dim">คูลดาวน์ถึง</span>
                    <span style={{ marginLeft: 'auto' }}>{selectedRow.cooldown_until ? thaiDateTime(selectedRow.cooldown_until) : '—'}</span>
                  </div>
                  <div className="row" style={{ gap: 10 }}>
                    <span className="dim">หมดอายุคำขอทดลอง</span>
                    <span style={{ marginLeft: 'auto' }}>{selectedRow.probe_expires_at ? thaiDateTime(selectedRow.probe_expires_at) : '—'}</span>
                  </div>
                  <div className="row" style={{ gap: 10 }}>
                    <span className="dim">ล้มเหลวล่าสุด</span>
                    <span style={{ marginLeft: 'auto' }}>{timeText(selectedRow.last_failure_at)}</span>
                  </div>
                  <div className="row" style={{ gap: 10 }}>
                    <span className="dim">สำเร็จล่าสุด</span>
                    <span style={{ marginLeft: 'auto' }}>{timeText(selectedRow.last_success_at)}</span>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--color-hairline)', marginTop: 16, paddingTop: 14 }}>
                  <div className="card-label" style={{ marginBottom: 10 }}>การดำเนินการของผู้ดูแล</div>
                  {pending ? (
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <span className="dim" style={{ fontSize: 12, width: '100%', marginBottom: 4 }}>
                        {pending === 'reset'
                          ? 'ยืนยันการรีเซ็ตเป็นปิดวงจรและล้างตัวนับทั้งหมด'
                          : `ยืนยันการบันทึกผล “${outcomeLabel[pending]}” ให้กับเป้าหมายนี้`}
                      </span>
                      <button
                        className="btn btn-sm"
                        disabled={busy}
                        style={pending === 'reset' ? { color: 'var(--color-crimson)' } : undefined}
                        onClick={() => {
                          if (!selectedRow) return
                          if (pending === 'reset') reset.mutate(selectedRow.target)
                          else record.mutate({ target: selectedRow.target, outcome: pending })
                        }}
                      >
                        {busy ? 'กำลังบันทึก…' : 'ยืนยัน'}
                      </button>
                      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setPending(null)}>
                        ยกเลิก
                      </button>
                    </div>
                  ) : (
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setPending('reset')}>
                        รีเซ็ตเป็นปิดวงจร
                      </button>
                      <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setPending('success')}>
                        บันทึกผลสำเร็จ
                      </button>
                      <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setPending('failure')}>
                        บันทึกผลล้มเหลว
                      </button>
                    </div>
                  )}
                  <p className="dim" style={{ fontSize: 11, lineHeight: 1.75, marginTop: 10 }}>
                    การบันทึกผลมีผลกับทุกอินสแตนซ์ทันที คำขอล้มเหลวติดกันถึงเกณฑ์จะเปิดวงจร
                    และคำขอทดลองที่ไม่มีโทเคนของคำขอปัจจุบันจะถูกเพิกเฉย
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="card card-pad">
            <div className="row" style={{ marginBottom: 14, gap: 10 }}>
              <span className="card-label">ประวัติการเปลี่ยนสถานะ</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11 }}>
                {history.data ? `${history.data.count} รายการ` : 'ใหม่สุดก่อน'}
              </span>
            </div>

            {!selected ? (
              <div className="dim" style={{ fontSize: 12.5 }}>เลือกเป้าหมายเพื่อดูประวัติ</div>
            ) : history.isLoading ? (
              <div className="stack"><Skel h={46} /><Skel h={46} /><Skel h={46} /></div>
            ) : history.isError ? (
              <div className="form-error">{errorText(history.error, 'โหลดประวัติไม่สำเร็จ')}</div>
            ) : (history.data?.events.length ?? 0) === 0 ? (
              <div className="dim" style={{ fontSize: 12.5, lineHeight: 1.8 }}>
                ยังไม่มีการเปลี่ยนสถานะของเป้าหมายนี้ — ประวัติจะเริ่มเมื่อวงจรเปิดครั้งแรก
              </div>
            ) : (
              <div className="stack" style={{ gap: 0 }}>
                {history.data?.events.map((event) => (
                  <div
                    key={event.id}
                    style={{ padding: '11px 0', borderBottom: '1px solid var(--color-hairline)' }}
                  >
                    <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                      <span className={`sev ${stateTone[event.from_state]}`}>
                        <i className="sev-dot" />
                        {stateName(event.from_state)}
                      </span>
                      <span className="dim">→</span>
                      <span className={`sev ${stateTone[event.to_state]}`}>
                        <i className="sev-dot" />
                        {stateName(event.to_state)}
                      </span>
                      <span className="chip" style={{ marginLeft: 'auto', fontSize: 10 }}>
                        {reasonLabel[event.reason] ?? event.reason}
                      </span>
                    </div>
                    <div className="row" style={{ gap: 8, marginTop: 6 }}>
                      <span className="dim" style={{ fontSize: 10.5 }}>{thaiDateTime(event.occurred_at)}</span>
                      {event.actor ? (
                        <span className="dim mono" style={{ fontSize: 10, marginLeft: 'auto' }}>{event.actor}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      <div className="card card-pad" style={{ marginTop: 18 }}>
        <div className="card-label">วิธีที่ตัวตัดวงจรนี้ทำงาน</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>สถานะเก็บในตาราง platform.breakers แยกตามองค์กรและเป้าหมาย ทุกอินสแตนซ์จึงใช้ตัวตัดวงจรเดียวกัน</li>
          <li>ปิดวงจร: คำขอผ่านและนับความล้มเหลวติดกัน เมื่อครบ {policy?.failure_threshold ?? 3} ครั้งจะเปิดวงจร</li>
          <li>เปิดวงจร: ปฏิเสธทุกคำขอจนถึงเวลาคูลดาวน์ โดยคูลดาวน์เพิ่มขึ้นแบบทวีคูณพร้อมสุ่มกระจายเมื่อเปิดซ้ำ</li>
          <li>ครึ่งวงจร: อนุญาตคำขอทดลองครั้งละหนึ่งคำขอ คำขออื่นจะถูกปฏิเสธพร้อมค่า Retry-After</li>
          <li>คำขอทดลองที่สำเร็จจะปิดวงจรและล้างตัวนับ หากล้มเหลวจะเปิดวงจรอีกครั้งด้วยคูลดาวน์ที่ยาวขึ้น</li>
          <li>โทเคนทดลองมีอายุจำกัด หากผู้เรียกหายไป ระบบจะออกโทเคนใหม่หลังหมดอายุแทนที่จะค้างที่ครึ่งวงจร</li>
          <li>ประวัติการเปลี่ยนสถานะเป็นบันทึกแบบต่อท้ายเท่านั้น ไม่มีคำสั่งแก้ไขหรือลบจากเส้นทาง API</li>
        </ul>
      </div>
    </>
  )
}
